import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Handshake, Loader2, Check, X, Building2, MapPin, Sparkles, Clock, Inbox,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const consentBadge = (s) => {
    if (s === "consented") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";
    if (s === "declined") return "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30";
    return "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30";
};
const consentLabel = (s) =>
    s === "consented" ? "Accepted" : s === "declined" ? "Declined" : "Awaiting your response";

const Opportunities = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingIds, setPendingIds] = useState(new Set());

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/professional/opportunities");
            setRows(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const respond = async (id, consent) => {
        setPendingIds((p) => new Set(p).add(id));
        try {
            await api.post(`/professional/opportunities/${id}/respond`, { consent });
            toast.success(consent
                ? "You've shared your Passport with this employer."
                : "You've declined this opportunity.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPendingIds((p) => {
                const s = new Set(p); s.delete(id); return s;
            });
        }
    };

    const awaiting = rows.filter((r) => r.consent_status === "pending" || !r.consent_status);
    const answered = rows.filter((r) => r.consent_status === "consented" || r.consent_status === "declined");

    return (
        <div data-testid="opportunities-page">
            <div className="mb-6">
                <p className="overline">Marketplace outreach</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Opportunities
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Employers have found you through the Talent Marketplace. Choose who gets access to your full Passport.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-12 text-center" data-testid="opportunities-empty">
                    <Inbox size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">No opportunities yet.</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                        When an employer discovers your Passport in the Marketplace, their request will appear here.
                        A stronger Passport = more inbound interest.
                    </p>
                </div>
            ) : (
                <div className="space-y-8">
                    {awaiting.length > 0 && (
                        <section>
                            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                                Awaiting your response ({awaiting.length})
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {awaiting.map((r) => (
                                    <OpportunityCard key={r.id} req={r} onRespond={respond} pending={pendingIds.has(r.id)} />
                                ))}
                            </div>
                        </section>
                    )}
                    {answered.length > 0 && (
                        <section>
                            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                                History ({answered.length})
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {answered.map((r) => (
                                    <OpportunityCard key={r.id} req={r} onRespond={respond} pending={pendingIds.has(r.id)} />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
};

const OpportunityCard = ({ req, onRespond, pending }) => {
    const isAwaiting = req.consent_status === "pending" || !req.consent_status;
    const [note, setNote] = useState("");
    const [showNote, setShowNote] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
            data-testid={`opportunity-${req.id}`}
            className="card-soft p-5 flex flex-col"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-foreground">
                        <Building2 size={14} className="text-emerald-500" />
                        <span className="font-heading font-semibold truncate">
                            {req.company_name || req.employer_name || "A Simonara employer"}
                        </span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                        For <span className="text-foreground font-semibold">{req.job_title}</span>
                    </div>
                </div>
                <Badge className={`text-[10px] border ${consentBadge(req.consent_status)}`}>
                    {consentLabel(req.consent_status)}
                </Badge>
            </div>

            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                    <Clock size={11} /> {req.created_at ? new Date(req.created_at).toLocaleString() : ""}
                </span>
                {req.source === "marketplace" && (
                    <span className="inline-flex items-center gap-1"><MapPin size={11} /> via Talent Marketplace</span>
                )}
            </div>

            {isAwaiting && (
                <>
                    <div className="mt-4 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground border border-border">
                        <div className="font-semibold text-foreground mb-1">What happens next?</div>
                        Accepting shares your full Passport (name, contact, resume) with this employer only.
                        Declining removes the request — they won&apos;t see your identity.
                    </div>
                    {showNote && (
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Optional message they'll see when you accept…"
                            rows={2}
                            className="mt-3 text-sm"
                            data-testid={`opportunity-note-${req.id}`}
                        />
                    )}
                    <div className="mt-4 flex items-center gap-2">
                        <button
                            onClick={() => onRespond(req.id, true)}
                            disabled={pending}
                            className="btn-green text-sm px-3 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid={`opportunity-accept-${req.id}`}
                        >
                            {pending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Accept & share Passport
                        </button>
                        <button
                            onClick={() => onRespond(req.id, false)}
                            disabled={pending}
                            className="btn-outline text-sm px-3 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid={`opportunity-decline-${req.id}`}
                        >
                            <X size={13} /> Decline
                        </button>
                        {!showNote && (
                            <button
                                onClick={() => setShowNote(true)}
                                className="text-xs text-muted-foreground hover:text-foreground ml-auto"
                                data-testid={`opportunity-add-note-${req.id}`}
                            >
                                + Add note
                            </button>
                        )}
                    </div>
                </>
            )}

            {req.consent_status === "consented" && (
                <div className="mt-4 rounded-xl bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-2">
                    <Sparkles size={13} /> Your Passport is shared. Check <a href="/inbox" className="underline font-semibold">Inbox</a> for next steps.
                </div>
            )}
            {req.consent_status === "declined" && (
                <div className="mt-4 rounded-xl bg-muted p-3 text-xs text-muted-foreground border border-border">
                    Declined. Your identity was never shared.
                </div>
            )}
        </motion.div>
    );
};

export default Opportunities;
